import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { PaymentOverviewViewMode } from '@/features/contract-and-cost';
import { PaymentOverviewFilterBar } from '@/features/contract-and-cost/payment';
import {
  getMonthsInPeriod,
  parsePeriodStart,
} from '@/features/contract-and-cost/period';
import {
  PaymentStatCards,
  PaymentSummaryChart,
} from '@/features/software/contract-and-cost/components';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { MonthlyPaymentTable } from './-components/MonthlyPaymentTable';
import { PaymentOverviewTable } from './-components/PaymentOverviewTable';
import {
  useSoftwareMonthlyPaymentList,
  useSoftwarePaymentOverviewList,
  useSoftwarePaymentOverviewSummary,
} from './-query';

const SearchSchema = z.object({
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
});

export const Route = createFileRoute(
  '/_staff/software/_layout/contract-and-cost/_contract-and-cost/payment/$period/',
)({
  component: PaymentOverviewPage,
  validateSearch: zodValidator(SearchSchema),
});

function PaymentOverviewPage() {
  const { period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.paymentOverview',
  });

  const isMonthly = search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  const [showSummary, setShowSummary] = useState(false);

  const [pagination, setPagination] = useState({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
  });

  const queryParams = {
    period,
    viewMode: search.viewMode,
    month: isMonthly ? currentMonth : undefined,
    page: pagination.page,
    pageSize: pagination.pageSize,
    freeText: search.freeText,
  } as const;

  const parsedPeriodStart = parsePeriodStart(period);
  const periodDate = new Date(
    Date.UTC(parsedPeriodStart.year, parsedPeriodStart.month - 1, 1),
  );

  const { data: summary } = useSoftwarePaymentOverviewSummary({
    targetYearMonth: periodDate.toISOString(),
  });
  const transitionQuery = useSoftwarePaymentOverviewList({
    period,
    targetYearMonth: periodDate.toISOString(),
    paymentComparisonTarget: search.comparisonTarget,
    page: pagination.page,
    pageSize: pagination.pageSize,
    freeText: search.freeText,
  });
  const monthlyQuery = useSoftwareMonthlyPaymentList({
    ...queryParams,
    viewMode: 'monthly',
  });

  const handlePageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, page }));
      navigate({
        search: (prev) => ({ ...prev, pageIndex: page - 1 }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      setPagination({ page: 1, pageSize });
      navigate({
        search: (prev) => ({ ...prev, pageIndex: 0, pageSize }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleViewModeChange = useCallback(
    (mode: PaymentOverviewViewMode) => {
      setPagination({ page: 1, pageSize: pagination.pageSize });
      navigate({
        search: (prev) => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate, currentMonth, pagination.pageSize],
  );

  const handleMonthChange = useCallback(
    (month: number) => {
      navigate({
        search: (prev) => ({ ...prev, month }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleComparisonTargetChange = useCallback(
    (target: 'PREVIOUS_YEAR' | 'PREVIOUS_MONTH') => {
      navigate({
        search: (prev) => ({ ...prev, comparisonTarget: target }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      navigate({
        search: (prev) => ({
          ...prev,
          freeText: e.target.value || undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      {showSummary && summary && (
        <VStack className="gap-5">
          <PaymentStatCards summary={summary} />
          <PaymentSummaryChart data={summary.months} months={monthOptions} />
        </VStack>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors"
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className="size-3.5"
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <PaymentOverviewFilterBar
        viewMode={search.viewMode}
        onViewModeChange={handleViewModeChange}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={handleMonthChange}
        monthOptions={monthOptions}
        searchValue={search.freeText ?? ''}
        onSearchChange={handleSearchChange}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={handleComparisonTargetChange}
      />

      {isMonthly ? (
        <MonthlyPaymentTable
          data={monthlyQuery.data?.items ?? []}
          isPending={monthlyQuery.isPending}
          totalCount={monthlyQuery.data?.count}
          pageCount={monthlyQuery.data?.maxPage}
          pagination={{
            pageIndex: pagination.page - 1,
            pageSize: pagination.pageSize,
          }}
          onPaginationChange={(next) => {
            setPagination({
              page: next.pageIndex + 1,
              pageSize: next.pageSize,
            });
            navigate({
              search: (prev) => ({
                ...prev,
                pageIndex: next.pageIndex,
                pageSize: next.pageSize,
              }),
              replace: true,
            });
          }}
          className="flex-1 min-h-0"
        />
      ) : (
        <PaymentOverviewTable
          data={transitionQuery.data?.items ?? []}
          isPending={transitionQuery.isPending}
          months={monthOptions}
          totalCount={transitionQuery.data?.count}
          pageCount={transitionQuery.data?.maxPage}
          currentPage={pagination.page}
          pageSize={pagination.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          className="flex-1 min-h-0"
        />
      )}
    </div>
  );
}
